/** @jsxImportSource @emotion/react */
import { PatientSection } from 'components/lab/patient/PatientDetails'
import { Patient } from 'store/types'
import { ClickablePhone } from 'components/atoms/ClickablePhone'
import { VoiceOptOutBadge } from 'components/atoms/VoiceOptOutBadge'
import { useGetPatientBalancesQuery } from 'store/services/patientTransactions'
import { useGetPatientReturnedMailQuery } from 'store/services/paperStatements'
import { StatePill } from 'components/lab/atoms/StatePill'
import { formatDate } from 'components/lab/billingPatientStatements/statementFormat'

interface Props {
  setMainSection: (section: PatientSection) => void
  mainSection: PatientSection
  patient: Patient
}

const formatCurrency = (amount: number) => {
  return `$${amount.toFixed(2)}`
}

const formatMailingAddress = (patient: Patient) => [
  patient?.primaryAddressStreetLine1,
  patient?.primaryAddressStreetLine2,
  [patient?.primaryAddressCity, [patient?.primaryAddressState, patient?.primaryAddressZip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', '),
].filter(Boolean).join(', ')

interface SectionButtonProps {
  setSection: (section: PatientSection) => void
  currentSection: PatientSection
  section: PatientSection
  label: string
}

const SectionButton = ({ setSection, currentSection, section, label }: SectionButtonProps) => (
  <button
    css={{
      background: 'transparent',
      border: 'none',
      color: currentSection === section ? '#000' : '#6D757E',
      fontSize: 18,
      borderBottom: currentSection === section ? '2px solid #000' : 'none',
      cursor: 'pointer',
      paddingBottom: 5,
      marginRight: 20,
    }}
    onClick={() => setSection(section)}>
    {label}
  </button>
)

export const PatientHeaderInfo = ({ setMainSection, mainSection, patient }: Props) => {
  const { data: balances } = useGetPatientBalancesQuery(
    patient?.id ? { patientId: patient.id } : { patientId: 0 },
    { skip: !patient?.id }
  )
  const { data: returnedMail } = useGetPatientReturnedMailQuery(patient?.id || 0, { skip: !patient?.id })

  return (
    <>
      <div
        className="d-flex mt-3"
        css={{
          gap: 40,
        }}>
        <div>
          <div css={{ color: '#6D757E' }}>Date of Birth</div>
          <div css={{ fontWeight: 600 }}>{patient?.dob}</div>
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Home Phone</div>
          <div css={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <ClickablePhone phoneNumber={patient?.homePhone} />
            <VoiceOptOutBadge optedOut={patient?.voiceOptOut} size={18} />
          </div>
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Email</div>
          <div css={{ fontWeight: 600 }}>{patient?.email}</div>
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Mailing Address</div>
          <div css={{ fontWeight: 600 }}>{formatMailingAddress(patient) || '--'}</div>
          {returnedMail?.returnedAt && (
            <StatePill
              color="#dc3545"
              label="Returned mail"
              tooltip={`A statement mailed ${formatDate(returnedMail.mailedAt)} came back on `
                + `${formatDate(returnedMail.returnedAt)}. Paper statements are held until this address is updated.`}
            />
          )}
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Patient Balance</div>
          <div
            css={{
              fontWeight: 600,
              color: Number(balances?.patientBalance || 0) > 0 ? '#dc3545' : '#000',
            }}>
            {formatCurrency(balances?.patientBalance || 0)}
          </div>
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Plan Balance</div>
          <div css={{ fontWeight: 600 }}>{formatCurrency(balances?.planBalance || 0)}</div>
        </div>
        <div title="Patient responsibility transferred to a secondary payer, pending its adjudication (part of the plan balance)">
          <div css={{ color: '#6D757E' }}>Transfers</div>
          <div css={{ fontWeight: 600 }}>{formatCurrency(balances?.transfersBalance || 0)}</div>
        </div>
        <div>
          <div css={{ color: '#6D757E' }}>Unapplied Payment</div>
          <div
            css={{
              fontWeight: 600,
              color: Number(balances?.unappliedPayment || 0) > 0 ? '#28a745' : '#000',
            }}>
            {formatCurrency(balances?.unappliedPayment || 0)}
          </div>
        </div>
      </div>
      <div className={'mt-4'}>
        <SectionButton
          setSection={setMainSection}
          section={PatientSection.Financial}
          currentSection={mainSection}
          label={'Financial'}
        />
        <SectionButton
          setSection={setMainSection}
          section={PatientSection.Orders}
          currentSection={mainSection}
          label={'Orders'}
        />
      </div>
    </>
  )
}
